import React, { useState, useEffect } from 'react';
import { subscriptionAPI } from '../../api/subscriptions';
import { useAuth } from '../../contexts/AuthContext';
import { CheckIcon } from '@heroicons/react/24/solid';

const SubscriptionPlans = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    loadPlansAndSubscription();
  }, []);

  const loadPlansAndSubscription = async () => {
    try {
      setLoading(true);
      const [plansRes, subRes] = await Promise.all([
        subscriptionAPI.getPlans(),
        subscriptionAPI.getMySubscription(user.business_id)
      ]);
      setPlans(plansRes.data.data);
      setCurrentPlan(subRes.data.data);
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!window.confirm(`Subscribe to ${planId.toUpperCase()} plan?`)) return;
    
    setSubscribing(true);
    try {
      await subscriptionAPI.subscribe({
        business_id: user.business_id,
        plan_id: planId,
        payment_method: 'credit_card',
        payment_token: 'tok_mock'
      });
      alert('Subscription updated successfully!');
      await loadPlansAndSubscription();
    } catch (error) {
      console.error('Failed to subscribe:', error);
      alert(error.response?.data?.message || 'Subscription failed');
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    
    try {
      await subscriptionAPI.cancel(user.business_id, 'Cancelled by user');
      alert('Subscription will be cancelled at the end of the billing period');
      await loadPlansAndSubscription();
    } catch (error) {
      console.error('Failed to cancel:', error);
    }
  };

  const handleResume = async () => {
    try {
      await subscriptionAPI.resume(user.business_id);
      alert('Subscription resumed successfully');
      await loadPlansAndSubscription();
    } catch (error) {
      console.error('Failed to resume:', error);
    }
  };

  if (loading) return <div className="text-center py-8">Loading plans...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Subscription Plans</h1>
        <p className="text-gray-600">Choose the perfect plan for your business</p>
      </div>

      {currentPlan && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-blue-800">
                <strong>Current Plan:</strong> {currentPlan.plan?.name}
                {currentPlan.is_trial && ` (Trial ends in ${currentPlan.trial_days_left} days)`}
              </p>
              {currentPlan.cancel_at_period_end && (
                <p className="text-sm text-orange-600 mt-1">
                  Subscription will end on {new Date(currentPlan.current_period_end).toLocaleDateString()}
                </p>
              )}
            </div>
            {currentPlan.status === 'active' && !currentPlan.cancel_at_period_end && (
              <button onClick={handleCancel} className="text-red-600 hover:text-red-800 text-sm">
                Cancel Subscription
              </button>
            )}
            {currentPlan.cancel_at_period_end && (
              <button onClick={handleResume} className="text-green-600 hover:text-green-800 text-sm">
                Resume Subscription
              </button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`bg-white rounded-lg shadow-md overflow-hidden ${
              currentPlan?.plan?.id === plan.id ? 'ring-2 ring-primary-500' : ''
            }`}
          >
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900">{plan.name}</h2>
              <p className="text-3xl font-bold mt-4">
                ${plan.price}
                <span className="text-base font-normal text-gray-500">/month</span>
              </p>
              
              <ul className="mt-6 space-y-3">
                <li className="flex items-center text-sm">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                  {plan.features.max_staff === 999999 ? 'Unlimited Staff' : `${plan.features.max_staff} Staff Members`}
                </li>
                <li className="flex items-center text-sm">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                  {plan.features.max_services === 999999 ? 'Unlimited Services' : `${plan.features.max_services} Services`}
                </li>
                <li className="flex items-center text-sm">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                  {plan.features.max_monthly_bookings === 999999 ? 'Unlimited Bookings' : `${plan.features.max_monthly_bookings} Bookings/mo`}
                </li>
                {plan.features.sms_notifications && (
                  <li className="flex items-center text-sm">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                    SMS Notifications
                  </li>
                )}
                {plan.features.api_access && (
                  <li className="flex items-center text-sm">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                    API Access
                  </li>
                )}
                {plan.features.priority_support && (
                  <li className="flex items-center text-sm">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                    Priority Support
                  </li>
                )}
                {plan.features.custom_branding && (
                  <li className="flex items-center text-sm">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                    Custom Branding
                  </li>
                )}
              </ul>
              
              <button
                onClick={() => handleSubscribe(plan.id)}
                disabled={currentPlan?.plan?.id === plan.id || subscribing}
                className={`mt-8 w-full py-2 px-4 rounded-lg transition-colors ${
                  currentPlan?.plan?.id === plan.id
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'btn-primary'
                }`}
              >
                {currentPlan?.plan?.id === plan.id ? 'Current Plan' : `Subscribe - $${plan.price}/mo`}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SubscriptionPlans;